import cx from 'classnames';
import { useEffect, useState } from 'react';

import { crush } from '@tedconf/js-crushinator-helpers';
import { LearnModule } from 'api';
import Icon from 'components/@tedui/Icon';
import { Heading } from 'components/typography';

import { FormattedMessage } from 'react-intl';
import LearnModuleCard from './LearnModuleCard';
import type LearnModuleListProps from './LearnModuleList.props';

const LearnModuleList = ({ learnModuleItems }: LearnModuleListProps) => {
  const [isShowingExcessModules, setIsShowingExcessModules] = useState(false);
  const MODULE_LIMIT = 3;
  // Default Learn Modules are those that fall within MODULE_LIMIT and display by default
  const [defaultLearnModules, setDefaultLearnModules] = useState<LearnModule[]>(
    []
  );
  // Excess Learn Modules are those that fall outside MODULE_LIMIT and display is manually toggled
  const [excessLearnModules, setExcessLearnModules] = useState<LearnModule[]>(
    []
  );
  const hasExcessModules = excessLearnModules?.length > 0;
  const MODULE_TOTAL = learnModuleItems.length;
  const chevronClasses = 'inline w-6 md:ml-2 md:w-5';

  useEffect(() => {
    setDefaultLearnModules(learnModuleItems.slice(0, MODULE_LIMIT));
    if (MODULE_TOTAL > MODULE_LIMIT) {
      setExcessLearnModules(learnModuleItems.slice(MODULE_LIMIT, MODULE_TOTAL));
    }
  }, [learnModuleItems, MODULE_TOTAL, MODULE_LIMIT]);

  const renderLearnModuleItems = (
    modules: LearnModule[],
    isDefaultLearnModule?: boolean
  ) => {
    return modules.map((module, index) => {
      const isLastDefaultItem =
        isDefaultLearnModule && index === modules.length - 1;
      const showDivider = hasExcessModules ? true : !isLastDefaultItem;

      const { headline, imageUrl } = module;
      const moduleImageUrl =
        imageUrl !== '' ? `${crush(imageUrl, { width: 200 })}` : null;

      return (
        <LearnModuleCard
          key={headline}
          {...module}
          moduleImageUrl={moduleImageUrl}
          showDivider={showDivider}
        />
      );
    });
  };

  return (
    <>
      <Heading size="m" className="mb-2 mt-12" tagName="h2">
        <FormattedMessage defaultMessage="Learn more" />
      </Heading>
      <div
        className={cx('mb-10 bg-gray-300 px-4 pt-8 md:mb-12', {
          'pb-8': !hasExcessModules
        })}
      >
        {renderLearnModuleItems(defaultLearnModules, true)}
        {hasExcessModules && (
          <>
            <div className={cx({ hidden: !isShowingExcessModules })}>
              {renderLearnModuleItems(excessLearnModules)}
            </div>
            <button
              type="button"
              className="mb-4"
              onClick={() => setIsShowingExcessModules(!isShowingExcessModules)}
            >
              <span className="text-gray-500">
                {`SHOW ${
                  isShowingExcessModules
                    ? 'LESS'
                    : `MORE [${MODULE_TOTAL - MODULE_LIMIT}]`
                }`}
              </span>
              {isShowingExcessModules ? (
                <Icon iconName="chevron-up" className={chevronClasses} />
              ) : (
                <Icon iconName="chevron-down" className={chevronClasses} />
              )}
            </button>
          </>
        )}
      </div>
    </>
  );
};

export default LearnModuleList;
